import { DonneesStructureBasePourParois, ParametresParoi, ParametresParois, PanneauParoi, Point3D, StructureParois, TypeMotifParoi } from '../types';

/**
 * Service de génération 3D pour les Parois
 * Génère des panneaux entre montants consécutifs en faisant le tour complet
 */
export class Parois3DService {
  dispose() {}

  genererParois(parametres: ParametresParois, donnees: DonneesStructureBasePourParois): StructureParois {
    const panneaux: PanneauParoi[] = [];

    const hauteurStruct = donnees.hauteur;
    const now = Date.now();

    const paroisActives = parametres.parois.filter((p) => p.actif);

    for (const p of paroisActives) {
      const y0 = Math.max(0, p.decalageSol);
      const y1 = Math.min(p.hauteur > 0 ? p.hauteur : hauteurStruct, hauteurStruct);
      if (y1 <= y0) continue;

      const step = Math.max(1, Math.floor(p.pasEntreMontants ?? 1));

      const pts = donnees.positionsMontants || [];
      if (!pts || pts.length < 2) continue;

      const sorted = this.trierMontantsParPerimetre(pts);
      const N = sorted.length;
      if (N < 2) continue;

      for (let i = 0; i < N; i++) {
        if (i % step !== 0) continue;
        const a = sorted[i];
        const b = sorted[(i + 1) % N];
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const len = Math.hypot(dx, dz);
        if (len < 1e-6) continue;

        // Génération selon le motif choisi
        const panneauxMotif = this.genererPanneauxPourMotif(p, a, b, y0, y1, i);
        panneaux.push(...panneauxMotif);
      }
    }

    return {
      id: `parois-${now}`,
      timestamp: now,
      parametres,
      panneaux,
      statistiques: {
        nombrePanneaux: panneaux.length,
        surfaceTotale: panneaux.reduce((acc, pn) => acc + pn.geometrie.surface, 0),
        materiauPrincipal: parametres.materiauDefaut,
        poidesEstime: panneaux.reduce((acc, pn) => acc + pn.geometrie.surface * (pn.parametres.epaisseur || 0.02) * 15, 0),
      },
    };
  }

  private genererPanneauxPourMotif(
    p: ParametresParoi,
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number },
    y0: number,
    y1: number,
    indexSegment: number
  ): PanneauParoi[] {
    switch (p.motif) {
      case TypeMotifParoi.PLEIN:
        return [this.creerPanneau(p, a, b, y0, y1, indexSegment)];

      case TypeMotifParoi.AJOURE_SIMPLE:
        return this.creerPanneauxAjourésSimples(p, a, b, y0, y1, indexSegment);

      case TypeMotifParoi.AJOURE_GEOMETRIQUE:
        return this.creerPanneauxAjourésGeometriques(p, a, b, y0, y1, indexSegment);

      case TypeMotifParoi.CLAUSTRAS:
        return this.creerPanneauxClaustras(p, a, b, y0, y1, indexSegment);

      case TypeMotifParoi.PERSONNALISE:
      default:
        // Pour l'instant, repli sur plein si motif personnalisé non encore implémenté
        console.warn('Motif personnalisé des parois non implémenté - utilisation du panneau plein');
        return [this.creerPanneau(p, a, b, y0, y1, indexSegment)];
    }
  }

  private creerPanneau(
    p: ParametresParoi,
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number },
    y0: number,
    y1: number,
    i: number,
    suffixId: string = ''
  ): PanneauParoi {
    const coins = this.creerRectangleVerticalEntrePoints(a, b, y0, y1);
    const centre = this.calculerCentre(coins);
    const normale = this.calculerNormaleSegment(a, b);

    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    const surface = Math.abs(len * (y1 - y0));

    return {
      id: `${p.id}-perimetre-${i}${suffixId}`,
      nom: `${p.nom} ${i + 1}`,
      geometrie: { coins, centre, normale, surface },
      parametres: p,
      vertices: this.coinsToVertices(coins),
      indices: [0, 1, 2, 2, 3, 0],
      materiaux: {
        diffuse: p.couleur,
        ambient: '#202020',
        specular: '#aaaaaa',
        shininess: 8,
        transparency: p.transparence,
      },
    };
  }

  private creerPanneauxAjourésSimples(
    p: ParametresParoi,
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number },
    y0: number,
    y1: number,
    i: number
  ): PanneauParoi[] {
    const nbLames = 6; // nombre de lames verticales
    const ratioRemplissage = 0.6; // 60% rempli, 40% vide

    const res: PanneauParoi[] = [];
    for (let k = 0; k < nbLames; k++) {
      const tDebut = k / nbLames;
      const tFin = Math.min(1, tDebut + (ratioRemplissage / nbLames));
      if (tFin <= tDebut) continue;

      const aSub = this.lerpPoint(a, b, tDebut);
      const bSub = this.lerpPoint(a, b, tFin);
      const coins = this.creerRectangleVerticalEntrePoints(aSub, bSub, y0, y1);

      const centre = this.calculerCentre(coins);
      const normale = this.calculerNormaleSegment(aSub, bSub);
      const dx = bSub.x - aSub.x;
      const dz = bSub.z - aSub.z;
      const surface = Math.abs(Math.hypot(dx, dz) * (y1 - y0));

      res.push({
        id: `${p.id}-perimetre-${i}-ajoure-${k}`,
        nom: `${p.nom} ${i + 1} - ajouré ${k + 1}`,
        geometrie: { coins, centre, normale, surface },
        parametres: p,
        vertices: this.coinsToVertices(coins),
        indices: [0, 1, 2, 2, 3, 0],
        materiaux: {
          diffuse: p.couleur,
          ambient: '#202020',
          specular: '#aaaaaa',
          shininess: 8,
          transparency: Math.min(1, p.transparence + 0.05),
        },
      });
    }
    return res;
  }

  private creerPanneauxAjourésGeometriques(
    p: ParametresParoi,
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number },
    y0: number,
    y1: number,
    i: number
  ): PanneauParoi[] {
    const nbCols = 6; // subdivisions suivant la longueur
    const nbRows = 4; // subdivisions suivant la hauteur
    const ratioCol = 0.8; // laisser un petit vide entre colonnes
    const ratioRow = 0.8; // laisser un petit vide entre rangées

    const res: PanneauParoi[] = [];
    for (let row = 0; row < nbRows; row++) {
      const yDeb = y0 + (row / nbRows) * (y1 - y0);
      const yFin = y0 + ((row + ratioRow) / nbRows) * (y1 - y0);

      for (let col = 0; col < nbCols; col++) {
        // motif damier: garder une case sur deux
        if ((row + col) % 2 !== 0) continue;

        const tDeb = col / nbCols;
        const tFin = Math.min(1, (col + ratioCol) / nbCols);
        if (tFin <= tDeb) continue;

        const aSub = this.lerpPoint(a, b, tDeb);
        const bSub = this.lerpPoint(a, b, tFin);
        const coins = [
          { x: aSub.x, y: yDeb, z: aSub.z },
          { x: bSub.x, y: yDeb, z: bSub.z },
          { x: bSub.x, y: yFin, z: bSub.z },
          { x: aSub.x, y: yFin, z: aSub.z },
        ];

        const centre = this.calculerCentre(coins);
        const normale = this.calculerNormaleSegment(aSub, bSub);
        const dx = bSub.x - aSub.x;
        const dz = bSub.z - aSub.z;
        const surface = Math.abs(Math.hypot(dx, dz) * (yFin - yDeb));

        res.push({
          id: `${p.id}-perimetre-${i}-geo-${row}-${col}`,
          nom: `${p.nom} ${i + 1} - géo ${row + 1}-${col + 1}`,
          geometrie: { coins, centre, normale, surface },
          parametres: p,
          vertices: this.coinsToVertices(coins),
          indices: [0, 1, 2, 2, 3, 0],
          materiaux: {
            diffuse: p.couleur,
            ambient: '#202020',
            specular: '#aaaaaa',
            shininess: 8,
            transparency: Math.min(1, p.transparence + 0.05),
          },
        });
      }
    }
    return res;
  }

  private creerPanneauxClaustras(
    p: ParametresParoi,
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number },
    y0: number,
    y1: number,
    i: number
  ): PanneauParoi[] {
    // Approche simple: lattes verticales + horizontales fines pour créer un treillis
    const nbCols = 8;
    const nbRows = 8;
    const epaisseurLatteCol = 0.25; // 25% de la cellule
    const epaisseurLatteRow = 0.25; // 25% de la cellule

    const res: PanneauParoi[] = [];

    // Lattes verticales
    for (let col = 0; col < nbCols; col++) {
      const tCentre = (col + 0.5) / nbCols;
      const demi = (epaisseurLatteCol / nbCols) / 2;
      const tDeb = Math.max(0, tCentre - demi);
      const tFin = Math.min(1, tCentre + demi);
      if (tFin <= tDeb) continue;

      const aSub = this.lerpPoint(a, b, tDeb);
      const bSub = this.lerpPoint(a, b, tFin);
      const coins = this.creerRectangleVerticalEntrePoints(aSub, bSub, y0, y1);

      const centre = this.calculerCentre(coins);
      const normale = this.calculerNormaleSegment(aSub, bSub);
      const dx = bSub.x - aSub.x;
      const dz = bSub.z - aSub.z;
      const surface = Math.abs(Math.hypot(dx, dz) * (y1 - y0));

      res.push({
        id: `${p.id}-perimetre-${i}-claustras-col-${col}`,
        nom: `${p.nom} ${i + 1} - claustras C${col + 1}`,
        geometrie: { coins, centre, normale, surface },
        parametres: p,
        vertices: this.coinsToVertices(coins),
        indices: [0, 1, 2, 2, 3, 0],
        materiaux: {
          diffuse: p.couleur,
          ambient: '#202020',
          specular: '#aaaaaa',
          shininess: 8,
          transparency: Math.min(1, p.transparence + 0.05),
        },
      });
    }

    // Lattes horizontales
    for (let row = 0; row < nbRows; row++) {
      const yCentre = y0 + ((row + 0.5) / nbRows) * (y1 - y0);
      const demiH = ((epaisseurLatteRow / nbRows) * (y1 - y0)) / 2;
      const yDeb = Math.max(y0, yCentre - demiH);
      const yFin = Math.min(y1, yCentre + demiH);
      if (yFin <= yDeb) continue;

      // pleine longueur de a->b
      const coins = [
        { x: a.x, y: yDeb, z: a.z },
        { x: b.x, y: yDeb, z: b.z },
        { x: b.x, y: yFin, z: b.z },
        { x: a.x, y: yFin, z: a.z },
      ];

      const centre = this.calculerCentre(coins);
      const normale = this.calculerNormaleSegment(a, b);
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const surface = Math.abs(Math.hypot(dx, dz) * (yFin - yDeb));

      res.push({
        id: `${p.id}-perimetre-${i}-claustras-row-${row}`,
        nom: `${p.nom} ${i + 1} - claustras R${row + 1}`,
        geometrie: { coins, centre, normale, surface },
        parametres: p,
        vertices: this.coinsToVertices(coins),
        indices: [0, 1, 2, 2, 3, 0],
        materiaux: {
          diffuse: p.couleur,
          ambient: '#202020',
          specular: '#aaaaaa',
          shininess: 8,
          transparency: Math.min(1, p.transparence + 0.05),
        },
      });
    }

    return res;
  }

  // Helpers
  private trierMontantsParPerimetre(montants: { x: number; y: number; z: number }[]) {
    if (!montants.length) return [] as { x: number; y: number; z: number }[];
    const cx = montants.reduce((s, p) => s + p.x, 0) / montants.length;
    const cz = montants.reduce((s, p) => s + p.z, 0) / montants.length;
    return [...montants].sort((a, b) => Math.atan2(a.z - cz, a.x - cx) - Math.atan2(b.z - cz, b.x - cx));
  }

  private lerpPoint(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }, t: number): { x: number; y: number; z: number } {
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      z: a.z + (b.z - a.z) * t,
    };
  }

  private creerRectangleVerticalEntrePoints(
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number },
    y0: number,
    y1: number
  ): Point3D[] {
    return [
      { x: a.x, y: y0, z: a.z },
      { x: b.x, y: y0, z: b.z },
      { x: b.x, y: y1, z: b.z },
      { x: a.x, y: y1, z: a.z },
    ];
  }

  private calculerCentre(points: Point3D[]): Point3D {
    const cx = (points[0].x + points[2].x) / 2;
    const cy = (points[0].y + points[2].y) / 2;
    const cz = (points[0].z + points[2].z) / 2;
    return { x: cx, y: cy, z: cz };
  }

  private calculerNormaleSegment(a: { x: number; z: number }, b: { x: number; z: number }): Point3D {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz) || 1;
    // normale horizontale vers l’extérieur approximée par rotation 90°
    return { x: -dz / len, y: 0, z: dx / len };
  }

  private coinsToVertices(points: Point3D[]): number[] {
    const v: number[] = [];
    for (const p of points) {
      v.push(p.x, p.y, p.z);
    }
    return v;
  }
}

export default Parois3DService;